<template>
  <div class="cs-page">
    <div class="cs-header">
      <div class="title">在线客服</div>
      <div class="sub">
        <template v-if="agentName">当前接待：{{ agentName }} · </template>
        工作时间 09:00 - 21:00
      </div>
    </div>

    <div ref="msgBox" class="cs-messages">
      <div v-if="!messages.length && !booting" class="empty-tip">正在连接客服…</div>
      <div
        v-for="m in messages"
        :key="m.id"
        class="msg"
        :class="m.sender_role"
      >
        <div class="meta" v-if="m.sender_role !== 'system'">
          {{ m.sender_role === 'agent' ? (m.sender_name || '客服') : '我' }}
          <span>{{ formatTime(m.created_at) }}</span>
        </div>
        <div class="bubble" :class="{ image: isImage(m) }">
          <img
            v-if="isImage(m)"
            :src="m.content"
            alt="图片"
            class="chat-img"
            @click="previewImage(m.content)"
          >
          <template v-else>{{ m.content }}</template>
        </div>
      </div>
    </div>

    <div class="cs-composer">
      <div class="img-btn" :class="{ disabled: sending }" @click="pickImage">
        <van-icon name="photo-o" size="22" />
      </div>
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        class="hidden-file"
        @change="onFileChange"
      >
      <van-field
        v-model="draft"
        rows="1"
        autosize
        type="textarea"
        maxlength="500"
        placeholder="请输入您的问题…（回车发送）"
        :border="false"
        @keydown.native="onComposerKeydown"
      />
      <van-button
        round
        size="small"
        color="linear-gradient(135deg,#1989fa,#0051d5)"
        :loading="sending"
        :disabled="!draft.trim()"
        @click="send"
      >发送</van-button>
    </div>
  </div>
</template>

<script>
import { ImagePreview } from 'vant'
import { csEnsureSession, csFetchMessages, csSendMessage, csSendImage } from '@/api/request'

export default {
  name: 'Service',
  data() {
    return {
      sessionId: null,
      agentName: '',
      messages: [],
      draft: '',
      sending: false,
      booting: true,
      lastMsgId: 0,
      pollTimer: null
    }
  },
  methods: {
    requireLogin() {
      if (localStorage.getItem('token')) return true
      this.$toast('请先登录后再咨询客服')
      this.$router.replace({ path: '/login', query: { redirect: '/service' } })
      return false
    },
    applySession(s) {
      if (!s) return
      this.sessionId = s.id
      this.agentName = s.agent_name || ''
    },
    isImage(m) {
      return m && (m.msg_type === 'image' || /^\/uploads\/cs\//.test(m.content || ''))
    },
    previewImage(url) {
      ImagePreview([url])
    },
    formatTime(t) {
      if (!t) return ''
      const s = String(t)
      return s.length > 16 ? s.slice(5, 16) : s
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.msgBox
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    async ensureSession() {
      const res = await csEnsureSession({})
      this.applySession(res.data)
      return res.data
    },
    async pull(reset = false) {
      const res = await csFetchMessages({
        session_id: this.sessionId,
        after_id: reset ? 0 : this.lastMsgId
      }, { silent: true })
      const data = res.data || {}
      if (data.session) this.applySession(data.session)
      const list = data.messages || []
      if (reset) {
        this.messages = list
      } else if (list.length) {
        this.messages = this.messages.concat(list)
      }
      if (this.messages.length) {
        this.lastMsgId = this.messages[this.messages.length - 1].id
      }
      if (reset || list.length) this.scrollBottom()
    },
    onComposerKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        this.send()
      }
    },
    pickImage() {
      if (this.sending) return
      if (!this.requireLogin()) return
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    async onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!this.requireLogin()) return
      if (!/^image\//.test(file.type)) {
        this.$toast('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast('图片不能超过 5MB')
        return
      }
      this.sending = true
      try {
        const fd = new FormData()
        fd.append('file', file)
        if (this.sessionId) fd.append('session_id', this.sessionId)
        const res = await csSendImage(fd)
        const data = res.data || {}
        if (data.session_id) this.sessionId = data.session_id
        await this.pull(false)
      } catch (err) {
        // toast by interceptor
      } finally {
        this.sending = false
      }
    },
    async send() {
      const text = this.draft.trim()
      if (!text || this.sending) return
      if (!this.requireLogin()) return
      this.sending = true
      try {
        const res = await csSendMessage({
          session_id: this.sessionId,
          content: text
        })
        const data = res.data || {}
        if (data.session_id) this.sessionId = data.session_id
        this.draft = ''
        await this.pull(false)
      } catch (e) {
        // toast by interceptor
      } finally {
        this.sending = false
      }
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => {
        this.pull(false).catch(() => {})
      }, 2500)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async boot() {
      if (!this.requireLogin()) {
        this.booting = false
        return
      }
      this.booting = true
      try {
        await this.ensureSession()
        await this.pull(true)
        this.startPoll()
      } catch (e) {
        this.$toast('客服连接失败，请稍后重试')
      } finally {
        this.booting = false
      }
    }
  },
  mounted() {
    this.boot()
  },
  activated() {
    this.boot()
  },
  deactivated() {
    this.stopPoll()
  },
  beforeDestroy() {
    this.stopPoll()
  }
}
</script>

<style lang="less" scoped>
.cs-page {
  /* 给底部 Tab(50px) + 全面屏安全区留空，避免输入框被挡住 */
  height: calc(100vh - 50px - env(safe-area-inset-bottom, 0px));
  height: calc(100dvh - 50px - env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #e8f3ff 0%, #f5f7fa 120px);
  box-sizing: border-box;
  padding-bottom: 8px;
}
.cs-header {
  padding: 14px 16px 10px;
  .title {
    font-size: 18px;
    font-weight: 700;
    color: #1989fa;
  }
  .sub {
    margin-top: 4px;
    font-size: 12px;
    color: #969799;
  }
}
.cs-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 12px;
  -webkit-overflow-scrolling: touch;
}
.empty-tip {
  text-align: center;
  color: #c8c9cc;
  font-size: 13px;
  padding: 40px 0;
}
.msg {
  margin-bottom: 12px;
  max-width: 82%;
  clear: both;
}
.msg.user {
  float: right;
  text-align: right;
}
.msg.agent,
.msg.system {
  float: left;
  text-align: left;
}
.msg.system {
  float: none;
  margin: 8px auto;
  max-width: 92%;
  text-align: center;
}
.meta {
  font-size: 11px;
  color: #969799;
  margin-bottom: 4px;
  span { margin-left: 6px; }
}
.bubble {
  display: inline-block;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff;
  color: #323233;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);
  text-align: left;
  &.image {
    padding: 4px;
    background: #fff !important;
    line-height: 0;
  }
}
.chat-img {
  max-width: 180px;
  max-height: 220px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
}
.msg.user .bubble:not(.image) {
  background: linear-gradient(135deg, #1989fa, #0051d5);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.msg.agent .bubble {
  border-bottom-left-radius: 4px;
}
.msg.system .bubble {
  background: transparent;
  box-shadow: none;
  color: #969799;
  font-size: 12px;
  padding: 4px 8px;
}
.cs-composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
  position: relative;
  z-index: 5;
  margin: 0 0 6px;
  padding: 10px 12px 12px;
  background: #fff;
  border-top: 1px solid #ebedf0;
  box-shadow: 0 -4px 12px rgba(25, 137, 250, 0.06);
  .img-btn {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1989fa;
    margin-bottom: 2px;
    &.disabled { opacity: .45; pointer-events: none; }
  }
  .hidden-file {
    display: none;
  }
  /deep/ .van-field {
    flex: 1;
    background: #f7f8fa;
    border-radius: 18px;
    padding: 6px 12px;
  }
  .van-button {
    flex-shrink: 0;
    height: 34px;
    padding: 0 16px;
    margin-bottom: 2px;
  }
}
</style>
